import { createUseStoreWithSelectorDeferred } from "@captions-web/utils/zustand-store";
import { fixWebmDuration } from "@fix-webm-duration/fix";
import { t } from "i18next";
import { toast } from "sonner";
import { createStore } from "zustand";
import { combine } from "zustand/middleware";

import { createStoreGetters } from "~/stores/root";

export const RECORDED_AUDIO_FILENAME = "__mirage-studio-recorded-audio__";

type StartCaptureParams = {
  audioDeviceId?: string;
};

export type RecordingStatus =
  | { type: "none" }
  | { type: "capturing"; countdown: number }
  | { type: "recording"; time: number }
  | { type: "error"; error: unknown };

export type AudioRecordingStoreValue = {
  status: RecordingStatus;
  intervalHandle: ReturnType<typeof setInterval> | null;
  stream: MediaStream | null;
  recorder: MediaRecorder | null;
  chunks: Blob[];
  startTimestamp: number | null;
};

const initialStoreValue: AudioRecordingStoreValue = {
  status: { type: "none" },
  intervalHandle: null,
  stream: null,
  recorder: null,
  chunks: [],
  startTimestamp: null,
};

export const createAudioRecordingStore = () => {
  return createStore(
    combine(initialStoreValue, (set, get) => {
      async function startCapture({ audioDeviceId }: StartCaptureParams) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: {
                exact: audioDeviceId,
              },
            },
          });

          set({ status: { type: "capturing", countdown: 3 }, stream });

          return stream;
        } catch (e) {
          return handleError("no-stream");
        }
      }

      async function startRecording() {
        const stream = get().stream;
        if (!stream) {
          return handleError("no-stream");
        }

        if (get().recorder) {
          return handleError("already-recording");
        }

        const recorder = new MediaRecorder(stream);

        recorder.ondataavailable = (e) => {
          set((state) => ({ chunks: [...state.chunks, e.data] }));
        };

        recorder.onerror = (e) => {
          console.error(e);
          handleError("recorder-error");
        };

        set({ recorder, chunks: [] });

        return new Promise<void>((resolve) => {
          recorder.onstart = () => {
            set({ status: { type: "recording", time: 0 }, startTimestamp: Date.now() });
            resolve();
          };

          recorder.start();
        }).catch(handleError);
      }

      function stopCapture() {
        if (get().status.type === "recording") {
          get().recorder?.stop();
        }

        const stream = get().stream;
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }

        set(initialStoreValue);
      }

      function cleanup() {
        clearInterval(get().intervalHandle ?? undefined);
        stopCapture();
      }

      function handleError(error: unknown) {
        cleanup();
        set({ status: { type: "error", error } });
        console.error(error);
        toast.error(t("mirage-studio:audio-creation.toasts.record-audio-failed"));
      }

      return {
        async init() {},
        cleanup,
        reset() {
          cleanup();
          set(initialStoreValue);
        },
        async startCountdown(params: StartCaptureParams) {
          const stream = startCapture(params).catch(handleError);
          if (!stream) {
            return;
          }

          const intervalHandle = setInterval(() => {
            const status = get().status;
            if (status.type === "none") {
              startCapture(params).catch(handleError);
            } else if (status.type === "capturing" && status.countdown > 1) {
              set({
                status: { type: "capturing", countdown: status.countdown - 1 },
              });
            } else if (status.type === "capturing") {
              startRecording().catch(handleError);
            } else if (status.type === "recording") {
              set({
                status: { type: "recording", time: status.time + 1 },
              });
            } else if (status.type === "error") {
              return handleError(status.error);
            } else {
              throw new UnreachableError(status);
            }
          }, 1000);

          set({ intervalHandle });
        },

        async stopRecording() {
          const { recorder, startTimestamp } = get();
          if (!recorder) {
            return handleError("no-recorder");
          }

          if (!startTimestamp) {
            return handleError("no-start-timestamp");
          }

          return new Promise<[Blob, string]>((resolve) => {
            recorder.onstop = async () => {
              const blob = new Blob(get().chunks, {
                type: recorder.mimeType,
              });

              const duration = Date.now() - startTimestamp;
              const fixedBlob = await fixWebmDuration(blob, duration);

              const [fileType] = recorder.mimeType.split(";");
              const [, fileExtension] = fileType.split("/");

              set((state) => ({
                recorder: null,
                // we have this check in case we've stopped capturing at some point while trying to stop recording
                status:
                  state.status.type === "recording"
                    ? { type: "capturing", countdown: 0 }
                    : state.status,
                startTimestamp: null,
              }));

              resolve([fixedBlob, fileExtension]);
            };

            recorder.stop();
          }).catch((e) => {
            console.error("error stopping recording", e);
            handleError("failed-to-stop-recording");
          });
        },
      };
    })
  );
};

type AudioRecordingStore = ReturnType<ReturnType<typeof createAudioRecordingStore>["getState"]>;

export const {
  getStore: getAudioRecordingStore,
  getStoreAsync: getAudioRecordingStoreAsync,
  getStoreApi: getAudioRecordingStoreApi,
} = createStoreGetters<AudioRecordingStore>("audio-recording");

export const useAudioRecordingStore = createUseStoreWithSelectorDeferred(() =>
  getAudioRecordingStoreApi()
);

class UnreachableError extends Error {
  constructor(value: never) {
    super(`Unexpected value: ${value}`);
  }
}
